import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';
import { UserDto } from '../../../auth/user.dto';
import { Achievement, labelsByAchievement } from '../../../shared/models/enums/achievement.enum';
import { ContractType } from '../../../shared/models/enums/contract-type.enum';
import { Pagination } from '../../../shared/pagination/pagination';
import { PaginationOptions } from '../../../shared/pagination/pagination-options';
import { BeneficiariesService } from '../../../shared/services/beneficiaries.service';
import { ContractsService } from '../../../shared/services/contracts.service';
import { UserService } from '../../../shared/services/user.service';
import { SortDirection, SortEvent } from '../../../shared/sortable/sort-event';
import { SortableDirective } from '../../../shared/sortable/sortable.directive';
import { ToastService } from '../../../shared/toast/toast.service';
import { Utils } from '../../../shared/utils';
import { BeneficiaryBaseComponent } from '../../beneficiary-base.component';
import { BeneficiaryDto } from '../../dto/beneficiary.dto';
import { ContractWrapperComponent } from './contract-wrapper/contract-wrapper.component';
import { ContractDto } from './contract.dto';
import { DisplayContract } from './displayContract';

@Component({
  selector: 'app-contract',
  templateUrl: './contract.component.html',
  standalone: false,
})
export class ContractComponent extends BeneficiaryBaseComponent implements OnInit, OnDestroy {
  $achievement = Utils.castTo<Achievement>();

  private static readonly resetContracts: Pagination<DisplayContract> = new Pagination([], 0, 0, 1, new PaginationOptions(1, 25));

  public user: UserDto;

  public readonly achievements: Map<Achievement, string> = labelsByAchievement;

  constructor(
    private readonly contractsService: ContractsService,
    private readonly modalService: NgbModal,
    private readonly authService: AuthService,
    private readonly toastService: ToastService,
    private readonly beneficiariesService: BeneficiariesService,
    private readonly userService: UserService,
  ) {
    super();
  }

  @ViewChildren(SortableDirective) headers: QueryList<SortableDirective>;
  public contracts: Pagination<DisplayContract> = ContractComponent.resetContracts;

  public async ngOnInit(): Promise<void> {
    this.user = await this.authService.getUser();

    this.beneficiariesService.beneficiary.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((beneficiary: BeneficiaryDto) => (this.beneficiary = beneficiary));

    await this.updateContracts(this.contracts.options);
    this.contractsService.contractUpdated$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateContracts(this.contracts.options);
    });
  }

  public async onSort(event: SortEvent): Promise<void> {
    // resetting other headers
    const column: string = event.column;
    const direction: SortDirection = event.direction;
    this.headers.forEach((header: SortableDirective) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });
    await this.updateContracts(this.contracts.options.withSort(column, direction == 'desc'));
  }

  public async changePage(pageNb?: number): Promise<void> {
    if (pageNb) {
      await this.updateContracts(this.contracts.options.withPage(pageNb));
    }
  }

  public async changeLimit(event: any): Promise<void> {
    if (event?.target?.value) {
      const limit: number = event.target.value;
      await this.updateContracts(this.contracts.options.withLimit(limit));
    }
  }

  public async openModal(contractId?: number): Promise<void> {
    if (!(await this.userService.getSignature())) {
      this.toastService.show('Veuillez enregistrer une signature dans votre profil', 'danger');
      return;
    }
    if (!this.user?.groups?.some((group: string) => ['ADMIN', 'TERRITORY', 'STRUCTURE', 'DIRECTION', 'DIRECTION+'].includes(group))) {
      this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
      return;
    }

    try {
      const contract: ContractDto = contractId ? await this.contractsService.findByContractId(contractId) : undefined;
      const modal: NgbModalRef = this.modalService.open(ContractWrapperComponent, {
        windowClass: 'rightModal',
        size: 'xl',
        backdrop: 'static',
      });
      const modalComponent: ContractWrapperComponent = modal.componentInstance as ContractWrapperComponent;
      await modalComponent.onInit(this.beneficiary, contract);
    } catch (error) {
      if (error.status === 437) {
        this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
      }
    }
  }

  private async updateContracts(pagination: PaginationOptions): Promise<void> {
    this.contracts = await this.contractsService.findByBeneficiaryId(this.beneficiary.id, pagination);
  }
}
